import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { login as loginRequest, getCurrentUser, rehydrateSession, clearFallbackTokens } from "./auth";
import { apiFetch, setAuthToken, setUnauthorizedHandler } from "./client";
import { AuthContext, type AuthContextValue } from "./auth-context"

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthContextValue["user"]>(null);
  // Start as "loading": on a cold mount we don't yet know whether the browser
  // holds a live refresh cookie. Deciding "unauthenticated" before the
  // rehydrate probe answers is what bounced every hard refresh to /login.
  const [status, setStatus] = useState<AuthContextValue["status"]>("loading");

  const login = useCallback(async (email: string, password: string) => {
    setStatus("loading");
    try {
      const { access_token } = await loginRequest(email, password);
      setAuthToken(access_token);
      const me = await getCurrentUser();
      setUser(me);
      setStatus("authenticated");
    } catch (err) {
      setAuthToken(null);
      setUser(null);
      setStatus("unauthenticated");
      throw err;
    }
  }, []);

  // End the session for real: the refresh token lives only in the HttpOnly
  // cookie, so the server-side family revocation AND the cookie expiry both
  // happen in POST /auth/logout. Clearing local state is just this tab's
  // cleanup (the in-memory access token must not outlive the click).
  const logout = useCallback(async () => {
    try {
      await apiFetch("/auth/logout", { method: "POST" });
    } catch {
      // Even on error the response expires the cookie; clear locally
      // regardless so this tab can't keep using its access token.
    }
    // Also drops the Safari-fallback ticket/body copy (sessionStorage) — the
    // fallback credential must never outlive the session.
    clearFallbackTokens();
    setAuthToken(null);
    setUser(null);
    setStatus("unauthenticated");
  }, []);

  // Re-establish the session on mount: after a hard refresh the access token
  // is gone from memory but the browser still holds the HttpOnly refresh
  // cookie. The probe rotates it and returns a fresh access token without the
  // refresh token ever being readable. StrictMode runs mount effects twice in
  // dev; the ref collapses them into one probe.
  const bootstrapped = useRef(false);
  useEffect(() => {
    if (bootstrapped.current) return;
    bootstrapped.current = true;
    (async () => {
      try {
        const result = await rehydrateSession();
        if (result.kind === "authenticated") {
          setAuthToken(result.accessToken);
          setUser(await getCurrentUser());
          setStatus("authenticated");
          return;
        }
        // "no_session" (never signed in) and "dead_session" (revoked/expired)
        // both end at the login screen — RequireRole handles the redirect.
      } catch {
        // API unreachable (ApiError status 0): stay on "loading" instead of
        // bouncing to /login — nobody can sign in to a dead API anyway, and
        // the refresh cookie is untouched.
        return;
      }
      setStatus("unauthenticated");
    })();
  }, []);

  useEffect(() => {
    setUnauthorizedHandler(logout);
    return () => setUnauthorizedHandler(null);
  }, [logout]);

  const value = useMemo(
    () => ({ user, status, login, logout }),
    [user, status, login, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}